import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { supabase } from "@/lib/supabase"

export default function ResetPasswordPage() {
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    // Check if we have a valid session from the reset link
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) {
        setError("Invalid or expired reset link. Please request a new one.")
      }
    })
  }, [])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setIsSuccess(false)

    if (password !== confirmPassword) {
      setError("Passwords don't match")
      return
    }

    if (password.length < 8) {
      setError("Password must be at least 8 characters")
      return
    }

    setIsLoading(true)

    try {
      const { error } = await supabase.auth.updateUser({
        password: password
      })

      if (error) throw error

      setIsSuccess(true)
      
      // Redirect to sign in after 2 seconds
      setTimeout(() => {
        navigate("/auth/sign-in")
      }, 2000)
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to reset password"
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="container flex items-center justify-center min-h-[calc(100vh-3.5rem)]">
      <div className="w-full max-w-md mx-auto">
        {/* Glassmorphism container */}
        <div className="rounded-3xl bg-white/5 backdrop-blur-lg border border-white/10 p-8 shadow-xl">
          {/* Header */}
          <div className="mb-6 text-center">
            <h1 className="text-2xl font-bold text-white mb-2">
              Set New Password
            </h1>
            <p className="text-sm text-white/60">
              Enter your new password below
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password" className="text-white/90 text-sm">New Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="At least 8 characters"
                className="rounded-full bg-white/10 border-white/20 text-white placeholder:text-white/40 h-11 px-4"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-white/90 text-sm">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                placeholder="Re-enter your password"
                className="rounded-full bg-white/10 border-white/20 text-white placeholder:text-white/40 h-11 px-4"
              />
            </div>
            {error && (
              <div className="rounded-full bg-red-500/10 border border-red-500/20 px-4 py-2.5 text-center">
                <p className="text-sm text-red-300">{error}</p>
              </div>
            )}
            {isSuccess && (
              <div className="rounded-full bg-green-500/10 border border-green-500/20 px-4 py-2.5 text-center">
                <p className="text-sm text-green-300">
                  Password reset successful! Redirecting...
                </p>
              </div>
            )}
            <Button
              type="submit"
              className="w-full rounded-full h-11 bg-primary hover:bg-primary/90 font-semibold"
              disabled={isLoading || isSuccess}
            >
              {isLoading ? "Resetting..." : "Reset Password"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  )
}

